import { Link, useNavigate } from "@remix-run/react"
import { useEffect, useState } from "react"
import { JourneyProgressBar } from "#app/components/journey-progress-bar.tsx"
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "#app/components/ui/alert-dialog.tsx"
import { Button } from "#app/components/ui/button.tsx"
import { Icon } from "#app/components/ui/icon.tsx"

export default function WheelOfBelongingIndex() {
	return (
		<>
			<WobHeader />
			<section className="mx-auto my-8 grid grid-cols-5 px-10 lg:max-w-[1600px] xl:px-6">
				<section className="col-span-5 gap-4 lg:col-span-2">
					<h1 className="pb-6 font-serif text-4xl lg:text-[60px] lg:leading-[76px]">
						Map out <i className="italic">Your</i> Belonging Journey
					</h1>
					<p className="pb-6 font-light leading-relaxed lg:pb-10 lg:text-[20px] lg:leading-[32px]">
						Welcome to your Belonging Journey. Use this tool to explore your
						unique experiences of belonging and connection. Consider an
						experience where you felt Belonging. What was your mindset when you
						first encountered the group? What did you hope for and what did you
						experience? How did those touch points build Belonging? Share your
						Belonging Journey, and discover how you are connected with others.
					</p>
					<ul className="space-y-4">
						<li className="mb-4">
							<Button
								size="wide"
								className="block w-full max-w-[500px] items-center rounded-3xl border border-[#174CFE] bg-transparent text-center font-light text-[#174CFE] hover:bg-[#174CFE] hover:text-white lg:text-[20px] lg:leading-[34px]"
								asChild
							>
								<Link to="/code" className="flex justify-center">
									I have an event code{" "}
									<Icon
										name="chevron-right"
										size="lg"
										className="mr-2 mt-0.5"
									/>
								</Link>
							</Button>
						</li>
						<li className="mb-4">
							<Button
								size="wide"
								className="block w-full max-w-[500px] items-center rounded-3xl border border-[#174CFE] bg-transparent text-center font-light text-[#174CFE] hover:bg-[#174CFE] hover:text-white lg:text-[20px] lg:leading-[34px]"
								asChild
							>
								<Link to="/no-code" className="flex justify-center">
									I don't have an event code{" "}
									<Icon
										name="chevron-right"
										size="lg"
										className="mr-2 mt-0.5"
									/>
								</Link>
							</Button>
						</li>
						<li className="mb-4">
							<Button
								size="wide"
								className="block w-full max-w-[500px] items-center rounded-3xl border border-[#174CFE] bg-transparent text-center font-light text-[#174CFE] hover:bg-[#174CFE] hover:text-white lg:text-[20px] lg:leading-[34px]"
								asChild
							>
								<Link to="/f/code" className="flex justify-center">
									I'm a facilitator{" "}
									<Icon
										name="chevron-right"
										size="lg"
										className="mr-2 mt-0.5"
									/>
								</Link>
							</Button>
						</li>
					</ul>
				</section>
				<section className="col-span-5 hidden lg:col-span-3 lg:block">
					<img
						src="/img/wheel_of_belonging_art.webp"
						alt="Wheel of Belonging illustration"
						className="-mt-16 ml-auto max-w-[850px]"
					/>
				</section>
			</section>
			<WobFooter />
		</>
	)
}

export const WobFooter = () => {
	return (
		<footer className="relative mt-40 flex justify-between bg-[#ECECED] p-[50px]">
			<section className="w-full">
				<p className="text=[#333032] max-w-[380px] pb-[30px] font-serif text-[16px] leading-[24px]">
					Storycraft Lab creates engagement by connecting human experience with
					your message.
				</p>
				<p className="text=[#333032] pb-[30px] text-[16px] leading-[24px]">
					art + science = engagement
				</p>
				<ul className="flex flex-row gap-x-4 border-b border-b-white pb-[52px]">
					<li>
						<a
							href="https://www.instagram.com/storycraft_lab/?hl=en"
							target="_blank"
							rel="noreferrer"
						>
							<button className="rounded-full bg-white p-2 shadow-md transition-colors duration-300 hover:bg-amber-200">
								<div className="h-[20px] w-[20px]">
									<img
										className="h-[20px] w-[20px]"
										alt="Instagram Icon"
										src="/img/instagram.svg"
									/>
								</div>
							</button>
						</a>
					</li>
					<li>
						<a
							href="https://www.facebook.com/StorycraftLab/"
							target="_blank"
							rel="noreferrer"
						>
							<button className="rounded-full bg-white p-2 shadow-md transition-colors duration-300 hover:bg-amber-200">
								<div className="h-[20px] w-[20px]">
									<img
										className="h-[20px] w-[20px]"
										alt="Facebook Icon"
										src="/img/facebook.svg"
									/>
								</div>
							</button>
						</a>
					</li>
					<li>
						<a
							href="https://www.linkedin.com/company/storycraft-lab"
							target="_blank"
							rel="noreferrer"
						>
							<button className="rounded-full bg-white p-2 shadow-md transition-colors duration-300 hover:bg-amber-200">
								<div className="h-[20px] w-[20px]">
									<img
										className="h-[20px] w-[20px]"
										alt="Linkedin Icon"
										src="/img/linkedin.svg"
									/>
								</div>
							</button>
						</a>
					</li>
				</ul>
				<p className="text=[#333032] pt-[23px] text-[16px] leading-[24px]">
					© 2024 Storycraft Lab. All rights reserved
				</p>
			</section>
		</footer>
	)
}

type JourneyStep = "Start" | "Touchpoint Selection" | "Confirm" | "Results"
export const WobHeader = ({ currentStep }: { currentStep?: JourneyStep }) => {
	return (
		<>
			<header className="mb-8 border-b border-slate-100" id="scroll">
				<img
					src="/img/scl_logo.png"
					alt="Storycraft Lab logo"
					className="mx-auto max-w-[200px] py-3"
				/>
			</header>
			{currentStep && (
				<div className="mx-auto w-[80%] pb-[4.5em] lg:max-w-[900px]">
					<JourneyProgressBar currentStep={currentStep} />
				</div>
			)}
		</>
	)
}

export const BelongingIdleAlert = ({ eventId }: { eventId?: string }) => {
	const [count, setCount] = useState(30)
	const navigate = useNavigate()

	// every 1 second, decrement the count
	const decrementCount = () => {
		setCount(prevCount => prevCount - 1)
	}

	useEffect(() => {
		if (count === 0) {
			if (eventId) navigate(`/${eventId}/start`)
			if (!eventId) navigate("/start")
		}
	}, [count, eventId, navigate])

	useEffect(() => {
		// start the countdown
		const interval = setInterval(decrementCount, 1000)

		// clean up the interval when the component unmounts
		return () => clearInterval(interval)
	}, [])

	return (
		<AlertDialog open>
			<AlertDialogContent className="max-w-[400px]">
				<AlertDialogHeader>
					<AlertDialogTitle>Still There?</AlertDialogTitle>
					<AlertDialogDescription>
						The quiz will reset after {count} seconds of inactivity.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter className="flex items-center justify-center gap-4">
					<AlertDialogCancel>Cancel</AlertDialogCancel>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}
